/**
 * HTML Sanitization Utility
 *
 * Provides secure HTML sanitization to prevent XSS attacks.
 * Uses DOMPurify to clean potentially dangerous HTML content.
 */

import DOMPurify from 'dompurify';

interface SanitizeOptions {
  ALLOWED_TAGS?: string[];
  ALLOWED_ATTR?: string[];
  KEEP_CONTENT?: boolean;
}

/**
 * Sanitize HTML content to prevent XSS attacks
 *
 * @param dirty - Potentially unsafe HTML content
 * @param options - Optional DOMPurify configuration
 * @returns Sanitized HTML string safe for rendering
 *
 * @example
 * ```tsx
 * <div dangerouslySetInnerHTML={{
 *   __html: sanitizeHtml(userContent)
 * }} />
 * ```
 */
export function sanitizeHtml(
  dirty: string | undefined | null,
  options?: SanitizeOptions
): string {
  if (!dirty) return '';

  const defaultOptions = {
    ALLOWED_TAGS: [
      'p', 'br', 'strong', 'em', 'u', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'ul', 'ol', 'li', 'a', 'img', 'blockquote', 'code', 'pre',
      'table', 'thead', 'tbody', 'tr', 'th', 'td', 'div', 'span'
    ],
    ALLOWED_ATTR: ['href', 'src', 'alt', 'title', 'class', 'id', 'target', 'rel'],
    KEEP_CONTENT: true,
  };

  const config = { ...defaultOptions, ...options };

  return DOMPurify.sanitize(dirty, config);
}

/**
 * Sanitize plain text content
 * Strips all HTML tags and returns plain text
 *
 * @param dirty - Potentially unsafe content
 * @returns Plain text with no HTML
 */
export function sanitizeText(dirty: string | undefined | null): string {
  if (!dirty) return '';

  return DOMPurify.sanitize(dirty, {
    ALLOWED_TAGS: [],
    KEEP_CONTENT: true,
  });
}

/**
 * Sanitize email content with additional restrictions
 *
 * @param dirty - Email HTML content
 * @returns Sanitized email HTML
 */
export function sanitizeEmail(dirty: string | undefined | null): string {
  if (!dirty) return '';

  return DOMPurify.sanitize(dirty, {
    ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 'a', 'ul', 'ol', 'li'],
    ALLOWED_ATTR: ['href', 'target', 'rel'],
    ALLOWED_URI_REGEXP: /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|sms|cid|xmpp):|[^a-z]|[a-z+.-]+(?:[^a-z+.-:]|$))/i,
  });
}

/**
 * Create a safe React component for rendering HTML
 *
 * @param content - HTML content to render
 * @param options - Sanitization options
 * @returns Object suitable for dangerouslySetInnerHTML
 */
export function createSafeHtml(
  content: string | undefined | null,
  options?: SanitizeOptions
): { __html: string } {
  return {
    __html: sanitizeHtml(content, options)
  };
}
